/*
 * Wishlist Client — adapter over the Wishlist King app runtime
 * (window.WishlistKing), exposing the Brora-internal contract:
 *   window.BroraWishlist.{ getItems, addItem, removeItem, getShareUrl }
 *   + the `brora:wishlist:changed` event ({ items }).
 *
 * THE SEAM: only this file touches window.WishlistKing. The drawer, the
 * card heart and the count badge talk solely to BroraWishlist + the event,
 * so they never know which app sits underneath. The app owns the session
 * (x-appmate-sid) and persistence via apis.wishlist
 * (loadWishlist/addItem/removeItem against routes.wishlistUrl).
 *
 * Item normalisation: the app returns { id, variantId, product: { id, title,
 * featured_image, images, url, handle, price_min, variants, options, … } }. We expose
 * snake_case (product_id from product.id, variant_id from variantId) + id (the
 * wishlist item id) so the existing callers (product-card-wishlist.js,
 * wishlist-drawer-actions.js) keep matching, and keep the full `product`
 * object so the drawer renderer + the quick-add build cards/size-pills without an
 * extra fetch. CONSUMED FIELDS the payload must keep: product.variants (id, option1,
 * available) and product.options (the "One Size" default-variant check) — dropping
 * either silently regresses the quick-add (no error).
 *
 * NOTE: the runtime is the legacy Wishlist King global (apis.wishlist:
 * loadWishlist/addItem/updateItem/removeItem/clearWishlist), NOT the newer
 * Swish `swishApp` browser SDK — that one is not served on this store.
 */
;(function () {
  'use strict';

  const READY_TIMEOUT_MS = 8000;
  const POLL_MS = 150;
  let cachedItems = null;
  let cachedPublicId = null;
  let inflightList = null;

  function emitChanged(items) {
    document.dispatchEvent(
      new CustomEvent('brora:wishlist:changed', { detail: { items: items || [] } })
    );
  }

  // The Wishlist King app loads async (app-loader.js), so window.WishlistKing
  // may not exist yet when this module runs. Poll until apis.wishlist is ready.
  let clientPromise = null;
  function getClient() {
    if (clientPromise) return clientPromise;
    clientPromise = new Promise(function (resolve) {
      const t0 = Date.now();
      (function poll() {
        const wl = window.WishlistKing && window.WishlistKing.apis && window.WishlistKing.apis.wishlist;
        if (wl) return resolve(wl);
        if (Date.now() - t0 > READY_TIMEOUT_MS) {
          console.warn('[wishlist-client] WishlistKing runtime not found — wishlist disabled.');
          return resolve(null);
        }
        setTimeout(poll, POLL_MS);
      })();
    });
    return clientPromise;
  }

  function normalize(it) {
    if (!it || typeof it !== 'object') return it;
    const product = it.product || {};
    return Object.assign({}, it, {
      id: it.id != null ? it.id : it.wishlistItemId,
      product_id: product.id != null ? product.id : (it.productId != null ? it.productId : it.product_id),
      variant_id: it.variantId != null ? it.variantId : it.variant_id
    });
  }

  function extractItems(res) {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.items)) return res.items;
    if (res.wishlist && Array.isArray(res.wishlist.items)) return res.wishlist.items;
    return [];
  }

  // The app keys reads/writes by wishlist id; the REST alias for the
  // visitor's default list is 'mine' (GET /wishlists/mine).
  const WISHLIST_ID = 'mine';
  // Handle of the CMS page carrying sections/main-wishlist.liquid — the target of a shared link.
  const WISHLIST_PAGE_HANDLE = 'wishlist';

  function readState() {
    const s = window.WishlistKing && window.WishlistKing.state;
    if (!s) return null;
    return s.wishlist || s;
  }

  async function loadOnce() {
    const wl = await getClient();
    if (!wl) return [];
    let res;
    try {
      // cacheStrategy 'no-store' bypasses the app's default cache
      // (max-age=60) so reads after add/remove reflect the true server
      // state — without it loadWishlist returns a stale list and the
      // drawer re-shows removed items (→ duplicate removes → 404).
      res = await wl.loadWishlist({ wishlistId: WISHLIST_ID, cacheStrategy: 'no-store' });
    } catch (err) {
      console.warn('[wishlist-client] loadWishlist({wishlistId}) failed, falling back to state:', err && err.message);
      res = readState();
    }
    // The wishlist container carries its own publicId — the shareable list id
    // (NOT 'mine', NOT the item ids). Captured here for getShareUrl().
    if (res && res.wishlist && res.wishlist.publicId != null) cachedPublicId = res.wishlist.publicId;
    return extractItems(res).map(normalize);
  }

  async function getItems(opts) {
    const force = opts && opts.force;
    if (cachedItems && !force) { emitChanged(cachedItems); return cachedItems; }
    if (inflightList) return inflightList;
    inflightList = (async function () {
      try {
        cachedItems = await loadOnce();
      } catch (err) {
        console.warn('[wishlist] getItems FAILED:', err);
        cachedItems = cachedItems || [];
      } finally {
        inflightList = null;
      }
      emitChanged(cachedItems);
      return cachedItems;
    })();
    return inflightList;
  }

  async function addItem(payload) {
    const wl = await getClient();
    if (!wl) { console.warn('[wishlist] addItem: client unavailable'); return cachedItems || []; }
    // Source contract: POST /wishlists/mine/items with { productId } (product-level).
    const body = {
      productId: payload && (payload.product_id != null ? payload.product_id : payload.productId)
    };
    if (body.productId == null) {
      console.warn('[wishlist] addItem: productId MISSING in payload — check data-wishlist-product-id on the card:', body);
    }
    try {
      const res = await wl.addItem(body);
      // Persist the anonymous session so a logged-out wishlist survives
      // navigation/reload. The low-level addItem only sets the in-memory
      // header; the durable store is localStorage 'wk_session_id', written by
      // the app-level WishlistKing.setSessionId.
      if (res && res.sessionId && window.WishlistKing && typeof window.WishlistKing.setSessionId === 'function') {
        window.WishlistKing.setSessionId(res.sessionId);
      }
      // Voyado favourite tracking — by productKey (= product id); logic + consent
      // gate live in voyado-client.js, so this stays a thin guarded call.
      if (window.VoyadoClient) window.VoyadoClient.notifyAddFavorite(body.productId);
    } catch (err) {
      console.warn('[wishlist] addItem ERROR for', body, err);
    }
    return getItems({ force: true });
  }

  async function removeItem(idOrItem) {
    const wl = await getClient();
    if (!wl) return cachedItems || [];
    const id = (idOrItem && typeof idOrItem === 'object')
      ? (idOrItem.id != null ? idOrItem.id : idOrItem.wishlistItemId)
      : idOrItem;
    // Product id for the Voyado favourite notify — from the passed item, else
    // resolved from the cached list by wishlist-item id (before the refetch).
    const removedProductId = (idOrItem && typeof idOrItem === 'object' && idOrItem.product_id != null)
      ? idOrItem.product_id
      : ((cachedItems || []).filter(function (it) { return String(it.id) === String(id); })[0] || {}).product_id;
    try {
      await wl.removeItem({ wishlistId: WISHLIST_ID, wishlistItemId: id });
      // Voyado favourite tracking (logic + consent gate in voyado-client.js).
      if (window.VoyadoClient) window.VoyadoClient.notifyRemoveFavorite(removedProductId);
    } catch (err) {
      const msg = String((err && err.message) || err);
      // 404 = item already gone → treat as removed (no warn). Other errors warn.
      if (!/\b404\b|not found/i.test(msg)) console.warn('[wishlist] removeItem ERROR for', id, err);
    }
    return getItems({ force: true });
  }

  // A shared list, read by its publicId rather than the 'mine' alias. Kept out of cachedItems so a
  // visitor opening someone else's link keeps their own. `isMine` is the app's ownership answer.
  async function loadShared(publicId) {
    const wl = await getClient();
    if (!wl || !publicId) return null;
    try {
      const res = await wl.loadWishlist({ wishlistId: publicId, cacheStrategy: 'no-store' });
      const container = (res && res.wishlist) || {};
      return { items: extractItems(res).map(normalize), isMine: container.isMine === true };
    } catch (err) {
      console.warn('[wishlist-client] loadShared failed for', publicId, err);
      return null;
    }
  }

  // Public shareable URL, pointing at the theme's wishlist page with the list id as ?list=. The
  // app's own /apps/wishlist/{publicId} is an App Proxy carrying no content section, so it renders
  // an empty page on this store. The market prefix comes from Shopify.routes.root, so the link opens
  // in the market the sharer was browsing. Null when the list id is unavailable.
  async function getShareUrl() {
    if (cachedPublicId == null) await getItems({ force: true });
    if (cachedPublicId == null) return null;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let url = window.location.origin + root + 'pages/' + WISHLIST_PAGE_HANDLE + '?list=' + encodeURIComponent(cachedPublicId);
    // The app returns no owner identity, so a signed-in sharer's first name travels in the link for
    // the reader's page title.
    const drawer = document.querySelector('[data-wishlist-drawer]');
    const name = drawer && (drawer.getAttribute('data-wishlist-owner-name') || '').trim();
    if (name) url += '&from=' + encodeURIComponent(name.slice(0, 40));
    return url;
  }

  window.BroraWishlist = { getItems: getItems, addItem: addItem, removeItem: removeItem, getShareUrl: getShareUrl, loadShared: loadShared };
})();
